import {
    useState
} from "react";
import {
    useWindowDimensions,
    StyleSheet,
    ScrollView,
    View
} from "react-native";
import {
    BrandMark
} from "./brand";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    CodeViewer,
    Separator,
    TextInput,
    Timeline,
    Command,
    Button,
    Badge,
    Table,
    Card,
    Chip,
    Item,
    Text,
    Kbd
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    CreditCard,
    UserPlus,
    Search,
    Check,
    Moon,
    Plus,
    Rss
} from "lucide-react-native";
import {
    createKitIcon
} from "../components/kitIcons";

const CheckIcon = createKitIcon(Check);

const SearchIcon = createKitIcon(Search);

const RssIcon = createKitIcon(Rss);

const PlusIcon = createKitIcon(Plus);

const UserPlusIcon = createKitIcon(UserPlus);

const CreditCardIcon = createKitIcon(CreditCard);

const MoonIcon = createKitIcon(Moon);

const BULLET = "•";

const TYPE_BADGES = {
    improve: "info",
    fix: "neutral",
    new: "success"
} as const;

const WEBHOOK_CODE = `POST /v2/webhooks
{
  "url": "https://nibgat.com/hooks/siparis",
  "events": ["order.created", "order.paid"],
  "retry": { "max_attempts": 8, "backoff": "exponential" }
}`;

const EXPORT_CODE = `curl https://api.nibgat.com/v2/reports/rpt_2841/export \\
  -H "Authorization: Bearer $NIBGAT_KEY" \\
  -d format=csv`;

const DELIVERIES = [
    {
        event: "order.paid",
        status: "delivered",
        id: "dlv_8a41",
        tries: 1
    },
    {
        event: "order.created",
        status: "retrying",
        id: "dlv_8a40",
        tries: 3
    },
    {
        event: "order.paid",
        status: "failed",
        id: "dlv_8a3f",
        tries: 8
    }
];

const STATUS_TYPES = {
    delivered: "success",
    retrying: "warning",
    failed: "danger"
} as const;

const RELEASES = [
    {
        date: new Date(2026, 9, 28),
        version: "v2.4.0",
        types: [
            "new",
            "improve"
        ],
        key: "r240",
        bullets: 3
    },
    {
        date: new Date(2026, 9, 14),
        version: "v2.3.2",
        key: "r232",
        bullets: 4,
        types: [
            "fix"
        ]
    },
    {
        date: new Date(2026, 8, 30),
        version: "v2.3.0",
        key: "r230",
        bullets: 3,
        types: [
            "new"
        ]
    },
    {
        date: new Date(2026, 8, 9),
        version: "v2.2.0",
        types: [
            "new",
            "improve",
            "fix"
        ],
        key: "r220",
        bullets: 4
    }
];

const FILTERS = [
    "all",
    "new",
    "improve",
    "fix"
];

const NAV_LINKS = [
    "docs",
    "api",
    "changelog",
    "status"
];

const stylesheet = StyleSheet.create({
    bar: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    versionRow: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row"
    },
    layout: {
        alignItems: "flex-start",
        width: "100%"
    },
    bullet: {
        alignItems: "flex-start",
        flexDirection: "row"
    },
    start: {
        alignSelf: "flex-start"
    },
    row: {
        alignItems: "center",
        flexDirection: "row"
    },
    wrap: {
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    page: {
        alignItems: "center",
        flexGrow: 1
    },
    frameInner: {
        alignSelf: "center",
        width: "100%"
    },
    main: {
        minWidth: 0,
        flex: 1,
        flexShrink: 1
    },
    bulletText: {
        minWidth: 0,
        flex: 1,
        flexShrink: 1
    },
    section: {
        width: "100%"
    },
    versions: {
        width: "100%"
    },
    side: {
        flexGrow: 0
    },
    search: {
        minWidth: 0
    }
});

const handlePress = () => undefined;

const Changelog01 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const {
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();

    const [
        filter,
        setFilter
    ] = useState("all");

    const [
        selected,
        setSelected
    ] = useState<string | undefined>(undefined);

    const [
        isSubscribed,
        setIsSubscribed
    ] = useState(false);

    const isWide = width >= shapeConfigs.wideBreakpoint;

    const isCompact = width < shapeConfigs.compactBreakpoint;

    const dateFormat = new Intl.DateTimeFormat(activeLocale, {
        day: "numeric",
        month: "short",
        year: "numeric"
    });

    const shortDateFormat = new Intl.DateTimeFormat(activeLocale, {
        month: "short",
        day: "numeric"
    });

    const visible = RELEASES.filter((release) => (filter === "all" || release.types.includes(filter)) && (selected === undefined || selected === release.key));

    const countOf = (type: string) => RELEASES.filter((release) => type === "all" || release.types.includes(type)).length;

    const handleFilterPress = (key: string) => () => {
        setFilter(key);

        setSelected(undefined);
    };

    const handleVersionPress = (key: string) => () => setSelected((previous) => previous === key ? undefined : key);

    const handleSubscribe = () => setIsSubscribed(true);

    const renderBullet = (release: typeof RELEASES[number], index: number) => <View
        style={[
            stylesheet.bullet,
            {
                gap: spaces.spacingSm
            }
        ]}
        key={index}
    >
        <Text
            variant="bodyMediumSize"
            color="mid"
        >
            {BULLET}
        </Text>
        {
            release.key === "r230" && index === 0 && <Kbd
                keys="mod+K"
                size="small"
            />
        }
        <Text
            style={stylesheet.bulletText}
            variant="bodyMediumSize"
            color="high"
        >
            {t(`blocks.changelog01.${release.key}.b${index + 1}`)}
        </Text>
    </View>;

    const renderDeliveries = () => <Card
        style={stylesheet.section}
        variant="filled"
    >
        <Card.Content>
            <View
                style={stylesheet.frameInner}
            >
                <Table
                    caption={t("blocks.changelog01.deliveries")}
                    isRaised={true}
                >
                    <Table.Header>
                        <Table.Row>
                            <Table.Head
                                isShrink={true}
                            >
                                {t("blocks.changelog01.colDelivery")}
                            </Table.Head>
                            <Table.Head>
                                {t("blocks.changelog01.colEvent")}
                            </Table.Head>
                            <Table.Head>
                                {t("blocks.changelog01.colStatus")}
                            </Table.Head>
                            <Table.Head
                                isNumeric={true}
                                isShrink={true}
                            >
                                {t("blocks.changelog01.colTries")}
                            </Table.Head>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {
                            DELIVERIES.map((delivery) => <Table.Row
                                key={delivery.id}
                            >
                                <Table.Cell
                                    isShrink={true}
                                >
                                    {delivery.id}
                                </Table.Cell>
                                <Table.Cell>
                                    {delivery.event}
                                </Table.Cell>
                                <Table.Cell>
                                    <Badge
                                        type={STATUS_TYPES[delivery.status as keyof typeof STATUS_TYPES]}
                                        title={t(`blocks.changelog01.status.${delivery.status}`)}
                                        variant="tonal"
                                        size="small"
                                    />
                                </Table.Cell>
                                <Table.Cell
                                    isNumeric={true}
                                    isShrink={true}
                                >
                                    {delivery.tries}
                                </Table.Cell>
                            </Table.Row>)
                        }
                    </Table.Body>
                </Table>
            </View>
        </Card.Content>
    </Card>;

    const renderPalette = () => <Card
        style={stylesheet.section}
        variant="filled"
    >
        <Card.Content>
            <View
                style={[
                    stylesheet.frameInner,
                    {
                        maxWidth: shapeConfigs.commandWidth
                    }
                ]}
            >
                <Command
                    variant="inline"
                >
                    <Command.Input
                        placeholder={t("blocks.changelog01.commandPlaceholder")}
                    />
                    <Command.List>
                        <Command.Group
                            title={t("blocks.changelog01.suggestions")}
                        >
                            <Command.Item
                                title={t("blocks.changelog01.cmdProject")}
                                onPress={handlePress}
                                icon={PlusIcon}
                                shortcut="N"
                            />
                            <Command.Item
                                title={t("blocks.changelog01.cmdInvite")}
                                onPress={handlePress}
                                icon={UserPlusIcon}
                                shortcut="I"
                            />
                            <Command.Item
                                title={t("blocks.changelog01.cmdBilling")}
                                icon={CreditCardIcon}
                                onPress={handlePress}
                                shortcut="B"
                            />
                            <Command.Item
                                title={t("blocks.changelog01.cmdTheme")}
                                onPress={handlePress}
                                icon={MoonIcon}
                                shortcut="T"
                            />
                        </Command.Group>
                    </Command.List>
                </Command>
            </View>
        </Card.Content>
    </Card>;

    const renderExtra = (key: string) => {
        if(key === "r240") {
            return <>
                <CodeViewer
                    languageLabel={t("blocks.changelog01.request")}
                    isLineNumberVisible={false}
                    code={WEBHOOK_CODE}
                    language="json"
                />
                {renderDeliveries()}
            </>;
        }

        if(key === "r230") {
            return renderPalette();
        }

        if(key === "r220") {
            return <CodeViewer
                isLineNumberVisible={false}
                code={EXPORT_CODE}
                language="bash"
            />;
        }

        return null;
    };

    const renderRelease = (release: typeof RELEASES[number], index: number) => <Timeline.Item
        icon={index === 0 ? CheckIcon : undefined}
        title={<View
            style={[
                stylesheet.wrap,
                {
                    gap: spaces.spacingSm
                }
            ]}
        >
            <Badge
                title={release.version}
                variant="outline"
                type="neutral"
                size="small"
            />
            <Text
                variant="bodySmallSize"
                color="mid"
            >
                {dateFormat.format(release.date)}
            </Text>
        </View>}
        isDot={index !== 0}
        key={release.key}
    >
        <View
            style={{
                paddingBottom: spaces.spacingXl,
                gap: spaces.spacingMd
            }}
        >
            <Text
                variant="headlineSmallSize"
                role="heading"
                color="high"
            >
                {t(`blocks.changelog01.${release.key}.title`)}
            </Text>
            <View
                style={[
                    stylesheet.wrap,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                {
                    release.types.map((type) => <Badge
                        type={TYPE_BADGES[type as keyof typeof TYPE_BADGES]}
                        title={t(`blocks.changelog01.type.${type}`)}
                        variant="tonal"
                        size="small"
                        key={type}
                    />)
                }
            </View>
            <View
                style={{
                    gap: spaces.spacingSm
                }}
            >
                {Array.from({
                    length: release.bullets
                }, (_, bulletIndex) => renderBullet(release, bulletIndex))}
            </View>
            {renderExtra(release.key)}
        </View>
    </Timeline.Item>;

    const renderFilter = () => <View
        style={{
            gap: spaces.spacingSm
        }}
    >
        <Text
            variant="labelMediumSize"
            color="mid"
        >
            {t("blocks.changelog01.filterTitle")}
        </Text>
        <View
            style={[
                stylesheet.wrap,
                {
                    gap: spaces.spacingSm
                }
            ]}
        >
            {
                FILTERS.map((key) => <Chip
                    title={`${t(`blocks.changelog01.type.${key}`)} ${countOf(key)}`}
                    onPress={handleFilterPress(key)}
                    isSelected={filter === key}
                    variant="outline"
                    type="neutral"
                    key={key}
                />)
            }
        </View>
    </View>;

    const renderSubscribe = () => <Card
        variant="outline"
    >
        <Card.Header>
            <Card.Title>
                {t("blocks.changelog01.subscribeTitle")}
            </Card.Title>
            <Card.Description>
                {t("blocks.changelog01.subscribeText")}
            </Card.Description>
        </Card.Header>
        <Card.Content>
            <View
                style={{
                    gap: spaces.spacingMd
                }}
            >
                <TextInput
                    placeholder={t("blocks.changelog01.emailPlaceholder")}
                    title={t("blocks.changelog01.email")}
                    keyboardType="email-address"
                    spreadBehaviour="stretch"
                    autoComplete="email"
                    inputMode="email"
                />
                <Button
                    title={t(isSubscribed ? "blocks.changelog01.subscribed" : "blocks.changelog01.subscribe")}
                    onPress={handleSubscribe}
                    spreadBehaviour="stretch"
                    isDisabled={isSubscribed}
                />
                <Separator />
                <Button
                    title={t("blocks.changelog01.rss")}
                    spreadBehaviour="stretch"
                    onPress={handlePress}
                    variant="outline"
                    type="neutral"
                    icon={RssIcon}
                />
            </View>
        </Card.Content>
    </Card>;

    const renderVersions = () => <View
        style={[
            stylesheet.versions,
            {
                gap: spaces.spacingSm
            }
        ]}
    >
        <Text
            variant="labelMediumSize"
            color="mid"
        >
            {t("blocks.changelog01.versionsTitle")}
        </Text>
        <Item.Group>
            {
                RELEASES.map((release) => <Item
                    accessibilityLabel={`${release.version}, ${shortDateFormat.format(release.date)}`}
                    onPress={handleVersionPress(release.key)}
                    isSelected={selected === release.key}
                    key={release.key}
                >
                    <Item.Content>
                        <View
                            style={stylesheet.versionRow}
                        >
                            <Text
                                variant="monoMediumSize"
                                color="high"
                            >
                                {release.version}
                            </Text>
                            <Text
                                variant="bodySmallSize"
                                color="mid"
                            >
                                {shortDateFormat.format(release.date)}
                            </Text>
                        </View>
                    </Item.Content>
                </Item>)
            }
        </Item.Group>
    </View>;

    return <ScrollView
        contentContainerStyle={stylesheet.page}
    >
        <View
            style={[
                stylesheet.bar,
                stylesheet.section,
                {
                    paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg,
                    paddingVertical: spaces.spacingSm,
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={[
                    stylesheet.row,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <BrandMark />
                <Badge
                    title={t("blocks.changelog01.developer")}
                    variant="outline"
                    type="neutral"
                    size="small"
                />
                {
                    isWide && NAV_LINKS.map((key) => <Button
                        variant={key === "changelog" ? "tonal" : "ghost"}
                        title={t(`blocks.changelog01.nav.${key}`)}
                        onPress={handlePress}
                        type="neutral"
                        size="small"
                        key={key}
                    />)
                }
            </View>
            <View
                style={[
                    stylesheet.row,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                {
                    !isCompact && <TextInput
                        accessibilityLabel={t("blocks.changelog01.search")}
                        placeholder={t("blocks.changelog01.search")}
                        style={[
                            stylesheet.search,
                            {
                                width: shapeConfigs.fieldMinWidth
                            }
                        ]}
                        spreadBehaviour="free"
                        icon={SearchIcon}
                        size="small"
                    />
                }
                <Button
                    title={t("blocks.changelog01.console")}
                    spreadBehaviour="free"
                    onPress={handlePress}
                    variant="outline"
                    type="neutral"
                    size="small"
                />
            </View>
        </View>
        <Separator />
        <View
            style={[
                stylesheet.section,
                {
                    maxWidth: shapeConfigs.wideBreakpoint + shapeConfigs.sideListWidth,
                    paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg,
                    paddingVertical: spaces.spacingXl,
                    gap: spaces.spacingXl
                }
            ]}
        >
            <View
                style={{
                    gap: spaces.spacingXs
                }}
            >
                <Text
                    variant="headlineMediumSize"
                    role="heading"
                    color="high"
                >
                    {t("blocks.changelog01.heading")}
                </Text>
                <Text
                    style={{
                        maxWidth: shapeConfigs.readingMaxWidth
                    }}
                    variant="bodyMediumSize"
                    color="mid"
                >
                    {t("blocks.changelog01.intro")}
                </Text>
            </View>
            {!isWide && renderFilter()}
            <View
                style={[
                    stylesheet.layout,
                    {
                        flexDirection: isWide ? "row" : "column",
                        gap: spaces.spacingXl
                    }
                ]}
            >
                <View
                    style={stylesheet.main}
                >
                    <Timeline
                        accessibilityLabel={t("blocks.changelog01.heading")}
                    >
                        {visible.map(renderRelease)}
                    </Timeline>
                </View>
                <View
                    style={[
                        isWide ? stylesheet.side : stylesheet.section,
                        {
                            width: isWide ? shapeConfigs.drawerWidth : "100%",
                            gap: spaces.spacingLg
                        }
                    ]}
                >
                    {renderSubscribe()}
                    {isWide && renderFilter()}
                    {renderVersions()}
                </View>
            </View>
        </View>
    </ScrollView>;
};

export default Changelog01;
